// features/chief-complaints/addUpdateChiefComplaint.modal.tsx
import NiceModal, { useModal } from '@ebay/nice-modal-react'
import { useEffect, useState } from 'react'
import { toast } from 'react-hot-toast'
import { useMutation, useQueryClient } from 'react-query'

import Button from 'components/form/button'
import Input from 'components/form/input'
import Modal from 'components/modal'
import { privateRequest } from 'config/axios.config'
import { errorHandler } from 'utils/errorHandler'

export type ChiefComplaint = {
    _id: string
    name: string
    type?: string
    company_name?: string
    createdAt: Date
    updatedAt: Date
}

interface Payload {
    name: string
}

interface UpdatePayload extends Payload {
    id: string
}

// Modal
export default NiceModal.create(
    ({ chiefComplaint, title }: { chiefComplaint?: ChiefComplaint; title: string }) => {
        const modal = useModal()
        const queryClient = useQueryClient()

        const [form, setForm] = useState({ name: '' })
        const [errors, setErrors] = useState<{ name?: string }>({})

        useEffect(() => {
            if (!chiefComplaint) return
            setForm({ name: chiefComplaint.name ?? '' })
        }, [chiefComplaint])

        const changeHandler = ({ target: { name, value } }: React.ChangeEvent<HTMLInputElement>) => {
            setForm((prev) => ({ ...prev, [name]: value }))
            setErrors({ ...errors, [name]: '' })
        }

        const addMutation = useMutation<{ message: string }, Error, Payload>(
            async (payload) => {
                try {
                    const res = await privateRequest.post('admin/chief-complaints', payload)
                    return res.data
                } catch (error) {
                    errorHandler(error)
                    throw error
                }
            },
            {
                onSuccess: () => {
                    queryClient.invalidateQueries('chief-complaints')
                    modal.remove()
                },
            }
        )

        const updateMutation = useMutation<{ message: string }, Error, UpdatePayload>(
            async (payload) => {
                try {
                    const res = await privateRequest.patch('admin/chief-complaints', payload)
                    return res.data
                } catch (error) {
                    errorHandler(error)
                    throw error
                }
            },
            {
                onSuccess: () => {
                    queryClient.invalidateQueries('chief-complaints')
                    modal.remove()
                },
            }
        )

        const submitHandler = (e: React.FormEvent) => {
            e.preventDefault()
            if (!form.name) {
                setErrors({ name: 'Name is required' })
                return
            }

            const payload = { name: form.name }

            if (chiefComplaint?._id) {
                toast.promise(updateMutation.mutateAsync({ ...payload, id: chiefComplaint._id }), {
                    loading: 'Updating...',
                    success: (res) => res.message ?? 'Updated successfully',
                    error: (err: any) => err.message ?? 'Something went wrong',
                })
            } else {
                toast.promise(addMutation.mutateAsync(payload), {
                    loading: 'Adding...',
                    success: (res) => res.message ?? 'Added successfully',
                    error: (err: any) => err.message ?? 'Something went wrong',
                })
            }
        }

        return (
            <Modal
                title={title}
                visible={modal.visible}
                onCancel={() => modal.remove()}
                className="max-w-2xl"
            >
                <form onSubmit={submitHandler} className="flex flex-col gap-6">
                    <Input
                        name="name"
                        label="Name"
                        placeholder="Enter Chief Complaints Name"
                        value={form.name}
                        onChange={changeHandler}
                        error={!!errors.name}
                        helpText={errors.name}
                    />
                    <Button fullWidth disabled={addMutation.isLoading || updateMutation.isLoading}>
                        Submit
                    </Button>
                </form>
            </Modal>
        )
    }
)
